import React, { PropsWithChildren, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { Button, Infotip, Skeleton, Typography } from 'foundations-library/components'
import StatusChip from './StatusChip'
import EnrollmentEvents from './EnrollmentEvents'
import { rejectedStatuses } from '../../utils/statuses'
import { EnrollmentType } from '../../interface/EnrollmentType'

const jss = (theme) => {
    const { colors, typography } = theme['foundations-library']
    return {
        content: {
            maxWidth: '1000px',
            alignItems: 'center',
            gap: '40px',
            padding: '20px',
            borderRadius: '10px',
            border: `1px solid ${colors.neutral[100]}`,
            margin: '0 auto',
            marginBottom: 40
        },
        titles: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        chip: {
            margin: ({ itemStatus }) => rejectedStatuses.includes(itemStatus) ? '15px 0' : '15px 0 40px 0'
        },
        mb40: {
            marginBottom: 40
        },
        mb15: {
            marginBottom: 15
        },
        buttonContainer: {
            textAlign: 'right',
            paddingBottom: 40
        },
        infotip: {
            maxWidth: '100%'
        },
        latestEvent: {
            color: colors.neutral[50],
            marginTop: 15,
            backgroundColor: colors.neutral[800],
            padding: 10,
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            ...typography.subtitle2,
            '& ul': {
                color: colors.neutral[200],
                marginBottom: 0,
                paddingLeft: 20,
                ...typography.body2

            }
        },
        rejectedEventLoader: {
            marginTop: 15
        }
    }
}

const useStyles = makeStyles(jss)

interface PropsInterface {
    item: Record<string, unknown>
    titleLeft?: string
    titleRight: string
    enrollmentType: EnrollmentType
    onGoBack?(): unknown
}

/**
 *
 */
const Preview = ({
    item,
    children,
    titleLeft = 'Review and Edit',
    titleRight,
    enrollmentType,
    onGoBack
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const history = useHistory()
    const status = item.registration_status || item.status
    const styles = useStyles({ itemStatus: status })
    const [events, setEvents] = useState<null | unknown[]>(null)

    const isEventToShowLoading = useMemo(() => rejectedStatuses.includes(status) && events === null, [status, events])

    const eventToShow = useMemo(() => {
        if (isEventToShowLoading) return ''

        // return `
        //  Your campaign needs something
        //     <ul>
        //     <li>The privacy policy does not mention that mobile opt-in will not be shared, and appears to allow for the sharing of data with other companies.</li>
        //     <li>No opt-in website or signup page was provided for review.</li>
        //     </ul>
        // `
        if (rejectedStatuses.includes(status) && events?.length) {
            const rejection = events.find((x) => x.eventName === 'Rejection')
            if (rejection) {
                return rejection?.description
            }
        }
        return events?.[0]?.description || item?.latest_event?.description
    }, [events, item])

    const handleOnGoBack = () => {
        if (onGoBack) return onGoBack()
        history.goBack()
    }

    const statusChipProps = {
        status: status,
        enrollmentType: enrollmentType,
        showPopover: false,
        itemsAttached: item.active_did_count || item.campaign_count, // for campaign || for brand
        'data-testid': 'preview-status-chip'
    }
    return <div data-testid='preview-container'>
        <div className={styles.content}>
            <div className={styles.titles}>
                <Typography variant='h5' data-testid='preview-title-left'>{titleLeft}</Typography>
                <Typography variant='h7' data-testid='preview-title-right'>{titleRight}</Typography>
            </div>
            <div className={styles.chip}>
                {
                    rejectedStatuses.includes(status)
                        ? <div>
                            <StatusChip {...statusChipProps} />
                            {
                                isEventToShowLoading
                                    ? <Skeleton className={styles.rejectedEventLoader} variant='text' height={40}/>
                                    : <div className={styles.latestEvent} dangerouslySetInnerHTML={{ __html: eventToShow }}/>
                            }
                        </div>
                        : <Infotip value={eventToShow} bottom className={styles.infotip}>
                            <StatusChip {...statusChipProps} />
                        </Infotip>
                }
            </div>
            {children}
            <EnrollmentEvents
                entityType={enrollmentType === EnrollmentType.Brand ? 'brand' : 'campaign'}
                entityId={enrollmentType === EnrollmentType.Brand ? item.csp_brand_id : item.csp_campaign_id}
                onEventsLoad={setEvents}
            />
        </div>
        <div className={styles.buttonContainer}>
            <Button onClick={handleOnGoBack} variant='outlined' color='neutral' data-testid='preview-back-button'>Back</Button>
        </div>
    </div>
}

export default Preview
